// src/components/gavion/privacy/api/supabasePrivacyMethods.ts
// Supabase implementation of PrivacyMethods (B7-T15). Admin-only server side: every RPC re-checks is_admin()
// (P0001 forbidden); configuration writes go through RLS (admins only); the export runs in gavion-privacy with
// the caller's JWT. Errors become PrivacyApiError(<stable code>) — never a message, never a status (a refused
// call must not log the user out). Twin: fakePrivacyMethods.ts.
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  resolveRetention,
  retentionError,
  type RetentionKey,
} from "@/components/gavion/domain/privacy";
import {
  PrivacyApiError,
  type ErasureResult,
  type ExportResult,
  type PersonMatch,
  type PrivacyMethods,
  type PrivacySettings,
} from "./types";

type Err = { code?: string; message?: string } | null | undefined;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CODE = /^[a-z_]{3,40}$/;
const obj = (v: unknown): Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
const strOrNull = (v: unknown) => (typeof v === "string" && v ? v : null);

/** P0001 messages are stable codes; 42501 (no EXECUTE / RLS) is forbidden; anything else is unknown. */
export const privacyRpcCode = (e: Err): string => {
  if (!e) return "unknown";
  if (e.code === "42501") return "forbidden";
  return e.code === "P0001" && CODE.test(e.message ?? "")
    ? (e.message as string)
    : "unknown";
};
const fail = (e: Err): never => {
  throw new PrivacyApiError(privacyRpcCode(e));
};

/** Residual scan rows; anything that is not an array is null = residual UNKNOWN (never shown as clean). */
export const mapResidual = (raw: unknown): ErasureResult["residual"] =>
  Array.isArray(raw)
    ? raw.map((x) => {
        const o = obj(x);
        return {
          table: String(o.table),
          column: String(o.column),
          hits: Number(o.hits),
        };
      })
    : null;

/** erase_person payload → UI type. `scan_needles` (personal values) is deliberately dropped. */
export const mapErasure = (raw: unknown): ErasureResult => {
  const r = obj(raw);
  return {
    counts: obj(r.counts) as Record<string, number>,
    purgeQueued: Number(r.purge_queued ?? 0),
    residual: mapResidual(r.residual),
  };
};

/** gavion-privacy 200 body → UI type. Only an http(s) link is accepted (it becomes an href). */
export const mapExport = (raw: unknown): ExportResult => {
  const r = obj(raw);
  if (typeof r.url !== "string" || !/^https?:\/\//i.test(r.url))
    throw new PrivacyApiError("export_failed");
  return {
    url: r.url,
    expiresAt: String(r.expires_at),
    files: Number(r.files),
    missing: Number(r.missing),
    bytes: Number(r.bytes),
  };
};

/** privacy_search_people rows → UI type. */
export const mapPeople = (raw: unknown): PersonMatch[] =>
  (Array.isArray(raw) ? raw : []).map((x) => {
    const o = obj(x);
    return {
      companyId: Number(o.company_id),
      companyName: String(o.company_name ?? ""),
      phone: strOrNull(o.phone),
      email: strOrNull(o.email),
      contacts: Number(o.contacts ?? 0),
      matchedContact: strOrNull(o.matched_contact),
    };
  });

/** Stable code of a functions.invoke error: transport → network, HTTP → the body's { error }. */
export async function functionErrorCode(error: unknown): Promise<string> {
  const e = obj(error);
  if (e.name === "FunctionsFetchError") return "network";
  const ctx = e.context;
  if (ctx instanceof Response) {
    try {
      const body = obj(await ctx.clone().json());
      if (typeof body.error === "string" && CODE.test(body.error))
        return body.error;
    } catch {
      // not JSON: fall through
    }
  }
  return "unknown";
}

export function createPrivacyMethods(
  getClient: () => SupabaseClient,
): PrivacyMethods {
  const config = async () => {
    const { data, error } = await getClient()
      .from("configuration")
      .select("config")
      .eq("id", 1)
      .maybeSingle();
    if (error) fail(error);
    return obj(data?.config);
  };
  return {
    async privacyGetSettings() {
      const g = obj((await config()).gavion);
      const po = obj(g.privacy_officer);
      return {
        officerName: typeof po.name === "string" ? po.name : "",
        officerEmail: typeof po.email === "string" ? po.email : "",
        ...resolveRetention(g),
      };
    },

    async privacySaveSettings(s: PrivacySettings) {
      const email = s.officerEmail.trim().toLowerCase();
      if (email && !EMAIL.test(email))
        throw new PrivacyApiError("invalid_email");
      for (const k of [
        "recordingDays",
        "transcriptDays",
        "auditDays",
      ] as RetentionKey[]) {
        const e = retentionError(k, s[k]);
        if (e) throw new PrivacyApiError(e);
      }
      const cfg = await config();
      const g = obj(cfg.gavion);
      const next = {
        ...cfg,
        gavion: {
          ...g,
          privacy_officer: {
            name: s.officerName.trim() || null,
            email: email || null,
          },
          recording: { ...obj(g.recording), retention_days: s.recordingDays },
          transcripts: {
            ...obj(g.transcripts),
            retention_days: s.transcriptDays,
          },
          audit: { ...obj(g.audit), retention_days: s.auditDays },
        },
      };
      // trg_configuration_audit writes the settings.update audit row (changed keys only).
      const { data, error } = await getClient()
        .from("configuration")
        .update({ config: next })
        .eq("id", 1)
        .select("id");
      if (error) fail(error);
      // RLS (admins only) filters the row out instead of raising.
      if (!data || data.length === 0) throw new PrivacyApiError("forbidden");
    },

    async privacySearchPeople(q) {
      if (q.trim().length < 2) throw new PrivacyApiError("query_too_short");
      // One admin-only RPC (20261012170000): companies AND contacts (name, emails, phones). A contact
      // without a company is never returned (whole-lead ruling).
      const { data, error } = await getClient().rpc("privacy_search_people", {
        p_q: q.slice(0, 120),
      });
      if (error) fail(error);
      return mapPeople(data);
    },

    async privacyExportPerson(companyId) {
      const { data, error } = await getClient().functions.invoke(
        "gavion-privacy",
        { body: { action: "export", company_id: companyId } },
      );
      if (error) throw new PrivacyApiError(await functionErrorCode(error));
      return mapExport(data);
    },

    async privacyErasePerson(companyId, reason, confirmName) {
      const sb = getClient();
      const { data, error } = await sb.rpc("erase_person", {
        p_company_id: companyId,
        p_reason: reason,
        p_confirm_name: confirmName,
      });
      if (error) fail(error);
      // The erasure has committed. The residual scan is its own call (a slow scan never rolls the erasure
      // back); its needles stay in memory only. Any scan failure = residual UNKNOWN (null), never clean.
      const needles = (
        Array.isArray(obj(data).scan_needles)
          ? (obj(data).scan_needles as unknown[])
          : []
      ).filter((n): n is string => typeof n === "string");
      let residual: ErasureResult["residual"] = null;
      try {
        // Contract drift (no needles): scanning for nothing would report "clean" - keep residual unknown.
        if (needles.length === 0) return { ...mapErasure(data), residual };
        const scan = await sb.rpc("privacy_residual_scan", {
          p_company_id: companyId,
          p_needles: needles,
        });
        if (!scan.error) residual = mapResidual(scan.data);
      } catch {
        residual = null;
      }
      return { ...mapErasure(data), residual };
    },

    async privacyListAudit({ page, perPage, action }) {
      let query = getClient()
        .from("v_audit_log")
        .select("id,at,action,entity_type,entity_id,actor_name", {
          count: "exact",
        })
        .order("at", { ascending: false })
        .order("id", { ascending: false })
        .range((page - 1) * perPage, page * perPage - 1);
      if (action) query = query.eq("action", action);
      const { data, error, count } = await query;
      if (error) fail(error);
      return {
        total: count ?? 0,
        rows: (data ?? []).map((a) => ({
          id: Number(a.id),
          at: String(a.at),
          action: String(a.action),
          actorName: a.actor_name ?? null,
          entityType: a.entity_type ?? null,
          entityId: a.entity_id == null ? null : String(a.entity_id),
        })),
      };
    },
  };
}
